import {Button, Dropdown, DropdownItem, Label, Separator} from 'platform/components';
import {Show, Space} from 'platform/foundation';

import {Fragment} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Payment, useGetCheckoutPaymentTemplatesQuery} from '@dms/api/sales';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import i18n from '@dms/i18n';
import {useTenant} from '@dms/shared';

import {getSafely, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CheckoutPaymentFormTypeV2} from '../types/CheckoutPaymentFormTypeV2';

interface CheckoutPaymentTemplateDropdownV2Props extends RequiredTestIdProps {
  checkoutId: string;
  payment: Payment;
  formApi: UseFormReturn<CheckoutPaymentFormTypeV2>;
  onTemplateSelect: () => void;
}

export function CheckoutPaymentTemplateDropdownV2(props: CheckoutPaymentTemplateDropdownV2Props) {
  const {tenant} = useTenant();

  const templateId = useWatch({control: props.formApi.control, name: 'templateId'});

  const {data: paymentTemplates} = useGetCheckoutPaymentTemplatesQuery({
    checkoutId: props.checkoutId,
    paymentId: props.payment.checkoutPaymentId,
  });

  const {data: authorizationProfiles} = useGetAuthorizationProfilesQuery(
    {'x-tenant': tenant?.id ?? ''},
    {skip: isNil(tenant)}
  );

  const authProfilesById = indexBy((p) => p.id, authorizationProfiles ?? []);

  const authProfileTemplates = (paymentTemplates ?? []).filter(
    (t): t is typeof t & {authorizationProfileId: string} => isNotNil(t.authorizationProfileId)
  );
  const defaultTemplates = (paymentTemplates ?? []).filter((t) => isNil(t.authorizationProfileId));
  const uniqueProfileIds = authProfileTemplates.reduce<string[]>((acc, t) => {
    const id = t.authorizationProfileId;
    return acc.includes(id) ? acc : [...acc, id];
  }, []);

  const handleTemplateSelect = (id: string) => {
    props.formApi.setValue('templateId', id);
    props.onTemplateSelect();
  };

  const selectedTemplate = (paymentTemplates ?? []).find((t) => t.id === templateId);
  const fallbackTemplate = (paymentTemplates ?? []).find((t) => t.isDefault);
  const templateButtonTitle =
    selectedTemplate?.title ?? fallbackTemplate?.title ?? i18n.t('general.labels.template');

  return (
    <Show when={isNotNilOrEmpty(paymentTemplates)}>
      <Dropdown
        isHeightLimited
        dropdownControl={
          <Button
            variant="link"
            rightIcon="navigation/expand_more"
            title={templateButtonTitle}
            data-testid={suffixTestId('templateDropdown', props)}
          />
        }
        data-testid={suffixTestId('templateDropdown-menu', props)}
      >
        {uniqueProfileIds.map((profileId, profileIndex) => {
          const profileTemplates = authProfileTemplates.filter(
            (t) => t.authorizationProfileId === profileId
          );
          const profileName = getSafely(authProfilesById, profileId)?.name;
          return (
            <Fragment key={profileId}>
              {profileIndex > 0 && <Space vertical={2} />}
              <Label>{profileName ?? i18n.t('general.labels.template')}</Label>
              {profileTemplates.map((template, index) => (
                <DropdownItem
                  key={template.id}
                  label={template.title}
                  onClick={() => handleTemplateSelect(template.id)}
                  data-testid={suffixTestId(
                    `authProfileTemplate[${profileIndex}][${index}]`,
                    props
                  )}
                />
              ))}
              {(profileIndex < uniqueProfileIds.length - 1 ||
                isNotNilOrEmpty(defaultTemplates)) && <Separator spacing={0} />}
            </Fragment>
          );
        })}
        <Show when={isNotNilOrEmpty(defaultTemplates)}>
          <Show when={isNotNilOrEmpty(uniqueProfileIds)}>
            <Space vertical={2} />
            <Label>{i18n.t('entity.authorizationProfiles.labels.withoutAuthorization')}</Label>
          </Show>
          {defaultTemplates.map((template, index) => (
            <DropdownItem
              key={template.id}
              label={template.title}
              onClick={() => handleTemplateSelect(template.id)}
              data-testid={suffixTestId(`defaultTemplate[${index}]`, props)}
            />
          ))}
        </Show>
      </Dropdown>
    </Show>
  );
}
